/**
 * TAILWIND CONTAINER KOMPONENTE
 *
 * Responsive Container mit automatischem Padding (px-4 -> sm:px-6 -> lg:px-8).
 *
 * Props:
 *   size?      - narrow (max-w-3xl, 768px, Formulare)
 *                default (max-w-5xl, 1024px, Standard)
 *                wide (default, max-w-7xl, 1280px, Dashboards)
 *                full (max-w-screen-2xl, 1536px, Tabellen)
 *                viewport (keine max-width)
 *   spacing?   - none (default, kein vertikales Padding)
 *                default (py-6 sm:py-8 lg:py-10, Standard-Seitenabstand)
 *                compact (py-4 sm:py-6, dichte Admin-Bereiche)
 *   noPadding? - boolean (default: false), responsive horizontales Padding deaktivieren
 *   adaptive?  - boolean (default: false), reagiert auf Content-Width-Toggle (nur Desktop lg+)
 *   className? - zusätzliche Klassen
 *
 * Usage:
 *   <TWContainer size="wide">...</TWContainer>
 *   <TWContainer size="wide" spacing="default">...</TWContainer>
 *   <TWContainer adaptive>...</TWContainer>
 *   <TWContainer size="narrow" noPadding>...</TWContainer>
 */

import clsx from 'clsx';
import React from 'react';

// ============================================
// DESIGN TOKENS (Zentrale Stelle für Anpassungen)
// ============================================
const containerStyles = {
  // Basis-Klassen (immer angewendet)
  base: 'mx-auto w-full',

  // Responsive Padding: Mobile -> Tablet -> Desktop
  // px-4 (16px) -> sm:px-6 (24px) -> lg:px-8 (32px)
  padding: 'px-4 sm:px-6 lg:px-8',

  // Größen-Varianten
  sizes: {
    /** 768px - Formulare, Artikel, schmale Inhalte */
    narrow: 'max-w-3xl',
    /** 1024px - Standard-Seiten, mittlere Inhalte */
    default: 'max-w-5xl',
    /** 1280px - Dashboards, breite Inhalte */
    wide: 'max-w-7xl',
    /** 1536px - Große Tabellen, volle Breite auf 4K */
    full: 'max-w-screen-2xl',
    /** Keine max-width - nutzt kompletten verfügbaren Platz */
    viewport: '',
  },

  // Vertikales Padding (Tailwind-Plus Standard-Seitenabstand)
  spacing: {
    /** Kein vertikales Padding (Default, Rückwärtskompatibilität) */
    none: '',
    /** Standard-Seitenabstand: py-6 -> sm:py-8 -> lg:py-10 */
    default: 'py-6 sm:py-8 lg:py-10',
    /** Kompakter Abstand für dichte Admin-Bereiche: py-4 -> sm:py-6 */
    compact: 'py-4 sm:py-6',
  },
};

// ============================================
// TYPES
// ============================================
export type ContainerSize = keyof typeof containerStyles.sizes;
export type ContainerSpacing = keyof typeof containerStyles.spacing;

interface TWContainerProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Maximale Breite des Containers */
  size?: ContainerSize;
  /**
   * Vertikales Padding (oben/unten) für Seitenabstand.
   * - none (default): kein vertikales Padding
   * - default: py-6 sm:py-8 lg:py-10 (Tailwind-Plus Standard)
   * - compact: py-4 sm:py-6 (dichte Admin-Bereiche)
   */
  spacing?: ContainerSpacing;
  /** Deaktiviert das responsive horizontale Padding */
  noPadding?: boolean;
  /**
   * Adaptive Breite: Responsiver Default-Container.
   * - Mobile/Tablet: volle Breite
   * - Desktop (lg+): max-w-screen-2xl (1536px)
   * - Ignoriert die `size` Prop wenn aktiv
   */
  adaptive?: boolean;
  /** Kinder-Elemente */
  children: React.ReactNode;
}

// ============================================
// CONTAINER KOMPONENTE
// ============================================
export function TWContainer({
  size = 'wide',
  spacing = 'none',
  noPadding = false,
  adaptive = false,
  className,
  children,
  ...props
}: TWContainerProps) {
  // Adaptive Mode: Desktop cappt bei 1536px, Mobile/Tablet volle Breite
  const adaptiveClasses = adaptive
    ? 'lg:max-w-screen-2xl'
    : containerStyles.sizes[size];

  return (
    <div
      {...props}
      className={clsx(
        containerStyles.base,
        adaptiveClasses,
        !noPadding && containerStyles.padding,
        containerStyles.spacing[spacing],
        className
      )}
    >
      {children}
    </div>
  );
}

// ============================================
// BEISPIEL-VERWENDUNG (als Kommentar)
// ============================================
/*
// Standard Dashboard-Seite (1280px) mit vertikalem Seitenabstand
<TWContainer size="wide" spacing="default">
  <TWPageHeader title="Dashboard" />
  <div className="bg-gray-900 rounded-xl p-6">...</div>
</TWContainer>

// Nur horizontaler Container ohne vertikales Padding (alte Nutzung, bleibt unverändert)
<TWContainer size="wide">
  <TWPageHeader title="Dashboard" />
  <div className="bg-gray-900 rounded-xl p-6">...</div>
</TWContainer>

// Formular-Seite (768px)
<TWContainer size="narrow">
  <div className="bg-gray-900 rounded-xl p-6">
    <form>...</form>
  </div>
</TWContainer>

// Große Datentabelle auf 4K (1536px)
<TWContainer size="full">
  <TWTable>...</TWTable>
</TWContainer>

// Verschachtelt: Breiter Container mit schmalem Inhalt
<TWContainer size="wide">
  <TWContainer size="narrow" noPadding>
    <div className="bg-gray-900 rounded-xl p-6">Schmaler Inhalt</div>
  </TWContainer>
</TWContainer>

// ADAPTIVE MODE: Responsiver Default-Container (max-w-screen-2xl auf Desktop)
// Mobile/Tablet: volle Breite, Desktop: gedeckelt bei 1536px.
<SidebarLayout showFooter>
  <TWContainer adaptive>
    <TWPageHeader title="Portfolio" />
  </TWContainer>

  <TWContainer adaptive spacing="default">
    <div className="space-y-6">
      // Content hier
    </div>
  </TWContainer>
</SidebarLayout>
*/
